import {useCallback, useMemo} from "react";
import {create} from "zustand";

type ScopeKey = string;

type ScopedFiltersState = {
  byScope: Record<ScopeKey, Record<string, unknown>>;
};

type ScopedFiltersActions = {
  setScopeFilters: (scopeKey: ScopeKey, filters: Record<string, unknown>) => void;
  patchScopeFilters: (scopeKey: ScopeKey, patch: Record<string, unknown>) => void;
  clearScopeFilters: (scopeKey: ScopeKey) => void;
  clearAllScopeFilters: () => void;
};

type ScopedFiltersStore = ScopedFiltersState & ScopedFiltersActions;

export const useScopedFiltersStore = create<ScopedFiltersStore>((set) => ({
  byScope: {},

  setScopeFilters: (scopeKey, filters) => {
    set((state) => ({
      byScope: {
        ...state.byScope,
        [scopeKey]: {...filters},
      },
    }));
  },

  patchScopeFilters: (scopeKey, patch) => {
    set((state) => ({
      byScope: {
        ...state.byScope,
        [scopeKey]: {
          ...(state.byScope[scopeKey] ?? {}),
          ...patch,
        },
      },
    }));
  },

  clearScopeFilters: (scopeKey) => {
    set((state) => {
      const next = {...state.byScope};
      delete next[scopeKey];
      return {byScope: next};
    });
  },

  clearAllScopeFilters: () => {
    set({byScope: {}});
  },
}));

export function useStoredScopedFilters(scopeKey: string | null | undefined) {
  return useScopedFiltersStore((state) => (scopeKey ? state.byScope[scopeKey] : undefined));
}

export function useScopedFilters<T extends Record<string, any>>(
  scopeKey: string | null | undefined,
  defaults: T,
) {
  const stored = useStoredScopedFilters(scopeKey);
  const setScopeFilters = useScopedFiltersStore((state) => state.setScopeFilters);
  const patchScopeFilters = useScopedFiltersStore((state) => state.patchScopeFilters);
  const clearScopeFilters = useScopedFiltersStore((state) => state.clearScopeFilters);

  const filters = useMemo(() => {
    return {
      ...defaults,
      ...(stored ?? {}),
    } as T;
  }, [defaults, stored]);

  const setFilters = useCallback(
    (patch: Partial<T>) => {
      if (!scopeKey) return;
      patchScopeFilters(scopeKey, patch as Record<string, unknown>);
    },
    [scopeKey, patchScopeFilters],
  );

  const replaceFilters = useCallback(
    (next: T) => {
      if (!scopeKey) return;
      setScopeFilters(scopeKey, next as Record<string, unknown>);
    },
    [scopeKey, setScopeFilters],
  );

  const clearFilters = useCallback(() => {
    if (!scopeKey) return;
    clearScopeFilters(scopeKey);
  }, [scopeKey, clearScopeFilters]);

  return useMemo(
    () => ({
      filters,
      setFilters,
      replaceFilters,
      clearFilters,
    }),
    [filters, setFilters, replaceFilters, clearFilters],
  );
}
